
import { useEffect } from 'react';
import { AppSettingsService } from '@/services/app-settings-service';

// List of important logos that should be preloaded
const ESSENTIAL_LOGO_DOMAINS = [
  "lbvhywxvthwgiebjsrlr.supabase.co", // Supabase storage
  "play-lh.googleusercontent.com",
];

// Static logos that are always loaded
const STATIC_LOGOS = [
  "/lovable-uploads/c5290104-a025-440a-87a0-1157af3806ed.png", // Sidebar logo
  "https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo_ctrader.png", // cTrader logo
  "https://play-lh.googleusercontent.com/z1qgps1oruxuJnPdEJjK_209y-TI2v0bTQeZf8DxKRz2cR0ULY5-YFzKknA3YzlB7g", // FXPro logo
  "https://play-lh.googleusercontent.com/EPrP4-hkROiDGPmLnMSKaFlfWHCI5HdV1EWXXVnrHezxYK74czgVhPOxn-psu5_T6A", // XM logo
  "https://play-lh.googleusercontent.com/S_LUow-89YSbQdIf0cYK2qWLVOLGCKkRkj9PRlBHzWwE80-znbQZs95TH96cCIbhDA", // IC Market logo
  "https://play-lh.googleusercontent.com/Zz9gWxJt7C8wPZ1jd-8O9HMWAkH4KO8OfKvwPinpvZPtYs4RcbC1ckwZQPj4HNB8GQ", // FTMO logo
  "/lovable-uploads/d8c04af7-018a-4e74-8091-9940d7e03ad7.png", // AI Prop logo
];

/**
 * Hook to preload essential app logos and apply the current logo from database
 */
export function useLogoPreloader() {
  // Add preconnect tags for common image domains
  useEffect(() => {
    ESSENTIAL_LOGO_DOMAINS.forEach(domain => {
      const linkEl = document.createElement('link');
      linkEl.rel = 'preconnect';
      linkEl.href = `https://${domain}`;
      linkEl.crossOrigin = 'anonymous';
      document.head.appendChild(linkEl);
    });
  }, []);
  
  // Load and apply current logo from database with cache busting
  useEffect(() => {
    const loadCurrentLogo = async () => {
      try {
        const currentLogoUrl = await AppSettingsService.getCurrentLogoUrl();
        
        if (currentLogoUrl) {
          // Add cache busting parameter
          const cacheBustUrl = `${currentLogoUrl}?v=${Date.now()}`;
          
          // Apply logo to favicon links
          const faviconLinks = document.querySelectorAll('link[rel*="icon"]');
          faviconLinks.forEach(link => {
            if (link instanceof HTMLLinkElement) {
              link.href = cacheBustUrl;
            }
          });

          // Update apple touch icons
          const appleIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
          appleIcons.forEach(icon => {
            if (icon instanceof HTMLLinkElement) {
              icon.href = cacheBustUrl;
            }
          });

          // Update meta tags
          const ogImage = document.querySelector('meta[property="og:image"]');
          if (ogImage) {
            ogImage.setAttribute('content', cacheBustUrl);
          }

          const twitterImage = document.querySelector('meta[name="twitter:image"]');
          if (twitterImage) {
            twitterImage.setAttribute('content', cacheBustUrl);
          }

          // Update manifest link with cache busting
          const manifestLink = document.querySelector('link[rel="manifest"]');
          if (manifestLink instanceof HTMLLinkElement) {
            manifestLink.href = `/api/manifest.json?v=${Date.now()}`;
          }

          // Force PWA to update by registering service worker update
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then(registrations => {
              registrations.forEach(registration => {
                registration.update();
              });
            });
          }

          // Store logo URL for manifest generation
          window.__currentLogoUrl = currentLogoUrl;
        }
      } catch (error) {
        console.error('Failed to load logo from database:', error);
      }
    };

    loadCurrentLogo();
  }, []);

  // Preload static images
  useEffect(() => {
    const cachedImages = window.__preloadedImages || {};
    window.__preloadedImages = cachedImages;

    STATIC_LOGOS.forEach(url => {
      // Skip if already cached
      if (cachedImages[url]) return;
      
      const img = new Image();
      img.onload = () => {
        cachedImages[url] = true;
      };
      img.onerror = () => {
        console.warn(`Failed to preload: ${url}`);
      };
      img.src = url;
    });
  }, []);
}
